import actions from "~/shared/actions";
import { FORSIKTIG, MODERAT, AKSJER, ALDERSTILPASSET, OFFENSIV, getSpareprofil } from "~/shared/components/investeringsvalg/spareprofiler";

const findSpareprofil = (fund) => {
	if (fund.fundName.includes("Forsiktig")) {
		return FORSIKTIG;
	}
	if (fund.fundName.includes("Moderat")) {
		return MODERAT;
	}
	if (fund.fundName.includes("Offensiv")) {
		return OFFENSIV;
	}
	if (fund.fundName.includes("Aksjer")) {
		return AKSJER;
	}
	if (fund.fundName.includes("Alderstilpasset")) {
		return ALDERSTILPASSET;
	}
	return null;
};

const defaultState = {
	selectedFunds: [],
	renteProportion: 0,
	shareProportion: 0,
	totalProportion: 0,
	reminder: false,
	fondsskolekrav: false,
	ignoreAnsweredRisikovurdering: false,
};

const getTotalProportion = (selectedFunds) => selectedFunds.reduce((a, b) => a + (b.proportion || 0), 0);

const getShareProportion = (selectedFunds) =>
	selectedFunds.reduce((a, b) => {
		let percentage = 0;
		if (b.fundType === "Aksjefond") {
			percentage = b.proportion || 0;
		}
		if (b.fundType === "Spareprofil") {
			percentage = (b.proportion || 0) * (getSpareprofil(findSpareprofil(b)).sharePercentage / 100);
		}
		if (b.fundType === "Kombinasjonsfond") {
			percentage = b.proportion * 0.5;
		}
		return percentage + a;
	}, 0);

const getRenteProportion = (selectedFunds) =>
	selectedFunds.reduce((a, b) => {
		let percentage = 0;
		if (b.fundType === "Rentefond") {
			percentage = b.proportion || 0;
		}
		if (b.fundType === "Spareprofil") {
			percentage = (b.proportion || 0) * ((100 - getSpareprofil(findSpareprofil(b)).sharePercentage) / 100);
		}
		if (b.fundType === "Kombinasjonsfond") {
			percentage = b.proportion * 0.5;
		}
		return percentage + a;
	}, 0);

const verifyRecommendation = (recommendedShareProportion, actualShareProportion, regions) => {
	const uniqueRegions = new Set(regions);

	return Math.abs(actualShareProportion - recommendedShareProportion) <= 10 && (uniqueRegions.has("Global") || uniqueRegions.size > 1);
};

export default function fundSwitchReducer(fullState = {}, action) {
	const polisenummer = action.data?.polisenummer;

	if (!polisenummer) {
		return fullState;
	}

	const policyState = fullState[polisenummer] || defaultState;

	const nextState = (() => {
		switch (action.type) {
			case actions.AGGREGATED_POLICY_RESPONSE: {
				const { policy } = action.data;

				return {
					...policyState,
					recommendation: policy.lagretAnbefaling?.recommendation,
					recommendedShareProportion: policy.lagretAnbefaling?.recommendedShareProportion,
					fondsskolekrav: policy.lagretAnbefaling?.fondsskolekrav || true,
				};
			}
			case actions.RISIKOVURDERING_COMPLETE: {
				const { recommendation, recommendedShareProportion } = action.data;

				return {
					...policyState,
					recommendation,
					recommendedShareProportion,
				};
			}
			case actions.SAVE_RECOMMENDATION_RESPONSE: {
				const { recommendation } = action.data.result;

				return {
					...policyState,
					recommendation,
					ignoreAnsweredRisikovurdering: false,
				};
			}
			case actions.OPEN_INVESTMENT_FUNDS_RESPONSE: {
				const { openInvestmentFunds } = action.data;

				const selectedFunds = openInvestmentFunds.filter((f) => f.proportion > 0);

				const totalProportion = getTotalProportion(selectedFunds);
				const shareProportion = getShareProportion(selectedFunds);
				const renteProportion = getRenteProportion(selectedFunds);

				const withinRecommendation = verifyRecommendation(
					policyState.recommendedShareProportion,
					shareProportion,
					selectedFunds.filter((f) => f.proportion).map((f) => f.region),
				);

				return {
					...policyState,
					selectedFunds,
					renteProportion: (renteProportion / totalProportion) * 100,
					shareProportion: (shareProportion / totalProportion) * 100,
					totalProportion,
					withinRecommendation,
				};
			}
			case actions.UPDATE_FUND_SELECTION: {
				const selectedFunds = action.data.selectedFunds;

				const totalProportion = getTotalProportion(selectedFunds);
				const shareProportion = getShareProportion(selectedFunds);
				const renteProportion = getRenteProportion(selectedFunds);

				const withinRecommendation = verifyRecommendation(
					policyState.recommendedShareProportion,
					shareProportion,
					selectedFunds.filter((f) => f.proportion).map((f) => f.region),
				);

				return {
					...policyState,
					selectedFunds,
					renteProportion: (renteProportion / totalProportion) * 100,
					shareProportion: (shareProportion / totalProportion) * 100,
					totalProportion,
					withinRecommendation,
				};
			}
			case actions.UPDATE_FUND_PROPORTION: {
				const { isin, value } = action.data;

				const selectedFunds = policyState.selectedFunds.map((fund) => ({
					...fund,
					proportion: fund.isin === isin ? value : fund.proportion,
				}));

				const totalProportion = getTotalProportion(selectedFunds);
				const shareProportion = getShareProportion(selectedFunds);
				const renteProportion = getRenteProportion(selectedFunds);

				const withinRecommendation = verifyRecommendation(
					policyState.recommendedShareProportion,
					shareProportion,
					selectedFunds.filter((f) => f.proportion).map((f) => f.region),
				);

				return {
					...policyState,
					selectedFunds,
					renteProportion: (renteProportion / totalProportion) * 100,
					shareProportion: (shareProportion / totalProportion) * 100,
					totalProportion,
					withinRecommendation,
				};
			}
			case actions.OPEN_INVESTMENT_FUNDS_UPDATE_RESPONSE: {
				return {
					...policyState,
					reminder: action.data.reminder,
				};
			}
			case actions.IGNORE_ARCHIVED_RISIKOVURDERING:
				return {
					...policyState,
					ignoreAnsweredRisikovurdering: true,
				};
			case actions.VISIT_FONDSSKOLEN:
				return {
					...policyState,
					fondsskolekrav: false,
				};
			default:
				return policyState;
		}
	})();

	return {
		...fullState,
		[polisenummer]: nextState,
	};
}
